/**
 * useClientSubscriptions Hook
 *
 * Manages client subscription data for trainers including CRUD operations
 * and real-time updates.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";
import { useToast } from "@/hooks/use-toast";
import { unwrapFunctionInvoke, getFriendlyMessage, logError } from "@/lib/error-utils";

export type SubscriptionStatus =
  | "active"
  | "trialing"
  | "past_due"
  | "paused"
  | "cancelled"
  | "incomplete"
  | "incomplete_expired";

export type SubscriptionInterval = "week" | "month" | "year";

export interface ClientSubscription {
  id: string;
  user_id: string;
  organization_id: string | null;
  client_id: string;
  name: string;
  description: string | null;
  amount_cents: number;
  currency: string;
  interval: SubscriptionInterval;
  interval_count: number;
  sessions_per_period: number | null;
  sessions_used_this_period: number;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  stripe_price_id: string | null;
  stripe_product_id: string | null;
  status: SubscriptionStatus;
  trial_end: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  canceled_at: string | null;
  cancel_at_period_end: boolean;
  cancellation_reason: string | null;
  paused_at: string | null;
  resumes_at: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  client?: {
    id: string;
    name: string;
    email: string | null;
  };
}

export interface CreateSubscriptionParams {
  client_id: string;
  name: string;
  description?: string;
  amount_cents: number;
  interval?: SubscriptionInterval;
  interval_count?: number;
  sessions_per_period?: number;
  trial_days?: number;
  send_email?: boolean;
}

export const SUBSCRIPTION_STATUS_CONFIG: Record<
  SubscriptionStatus,
  { label: string; color: string; bgColor: string }
> = {
  active: { label: "Active", color: "text-success", bgColor: "bg-success/15" },
  trialing: { label: "Trial", color: "text-info", bgColor: "bg-info/15" },
  past_due: { label: "Past Due", color: "text-destructive", bgColor: "bg-destructive/15" },
  paused: { label: "Paused", color: "text-warning", bgColor: "bg-warning/15" },
  cancelled: { label: "Cancelled", color: "text-muted-foreground", bgColor: "bg-muted" },
  incomplete: { label: "Incomplete", color: "text-warning", bgColor: "bg-warning/15" },
  incomplete_expired: { label: "Expired", color: "text-muted-foreground", bgColor: "bg-muted" },
};

export const SUBSCRIPTION_INTERVAL_CONFIG: Record<
  SubscriptionInterval,
  { label: string; plural: string }
> = {
  week: { label: "Weekly", plural: "weeks" },
  month: { label: "Monthly", plural: "months" },
  year: { label: "Yearly", plural: "years" },
};

interface UseClientSubscriptionsOptions {
  clientId?: string;
  status?: SubscriptionStatus | SubscriptionStatus[];
}

export function useClientSubscriptions(options: UseClientSubscriptionsOptions = {}) {
  const { organization } = useOrganization();
  const [subscriptions, setSubscriptions] = useState<ClientSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!organization?.id) {
        setSubscriptions([]);
        setLoading(false);
        return;
      }

      let query = supabase
        .from("client_subscriptions")
        .select(`
          *,
          client:clients(id, name, email)
        `)
        .eq("organization_id", organization.id)
        .order("created_at", { ascending: false });

      // Apply filters
      if (options.clientId) {
        query = query.eq("client_id", options.clientId);
      }

      if (options.status) {
        if (Array.isArray(options.status)) {
          query = query.in("status", options.status);
        } else {
          query = query.eq("status", options.status);
        }
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setSubscriptions(data || []);
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch subscriptions"));
    } finally {
      setLoading(false);
    }
  }, [options.clientId, options.status, organization?.id]);

  // Initial fetch
  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel("client-subscriptions-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "client_subscriptions",
        },
        () => {
          fetchSubscriptions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchSubscriptions]);

  const createSubscription = async (params: CreateSubscriptionParams) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const result = await supabase.functions.invoke("create-client-subscription", {
        body: params,
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      const data = unwrapFunctionInvoke(result);

      toast({
        title: "Subscription created",
        description: `Subscription "${params.name}" has been created`,
      });

      await fetchSubscriptions();
      return data;
    } catch (err) {
      const f = getFriendlyMessage(err);
      logError(err, "create-client-subscription");
      toast({
        title: f.title,
        description: `${f.userMessage} (code: ${f.code})`,
        variant: "destructive",
      });
      throw err;
    }
  };

  const cancelSubscription = async (subscriptionId: string, cancelImmediately = false) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      // Must go through the edge function so Stripe is actually told to stop
      // billing. Updating only the local row used to leave Stripe charging
      // the card while the UI showed "cancelled".
      const result = await supabase.functions.invoke(
        "cancel-client-subscription",
        {
          body: {
            subscription_id: subscriptionId,
            cancel_immediately: cancelImmediately,
          },
        },
      );

      unwrapFunctionInvoke(result);

      toast({
        title: cancelImmediately ? "Subscription cancelled" : "Cancellation scheduled",
        description: cancelImmediately
          ? "Stripe has been instructed to stop billing immediately."
          : "The subscription will be cancelled at the end of the billing period.",
        duration: 6000,
      });

      await fetchSubscriptions();
    } catch (err) {
      const f = getFriendlyMessage(err);
      logError(err, "cancel-client-subscription");
      toast({
        title: f.title,
        description: `${f.userMessage} (code: ${f.code})`,
        variant: "destructive",
      });
      throw err;
    }
  };

  const pauseSubscription = async (subscriptionId: string, resumeDate?: string) => {
    try {
      const { error } = await supabase
        .from("client_subscriptions")
        .update({
          status: "paused",
          paused_at: new Date().toISOString(),
          resumes_at: resumeDate || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", subscriptionId);

      if (error) throw error;

      toast({
        title: "Subscription paused",
        description: resumeDate
          ? `Subscription will resume on ${new Date(resumeDate).toLocaleDateString()}`
          : "Subscription has been paused",
      });

      await fetchSubscriptions();
    } catch (err) {
      console.error("Error pausing subscription:", err);
      toast({
        title: "Error",
        description: "Failed to pause subscription",
        variant: "destructive",
      });
      throw err;
    }
  };

  const resumeSubscription = async (subscriptionId: string) => {
    try {
      const { error } = await supabase
        .from("client_subscriptions")
        .update({
          status: "active",
          paused_at: null,
          resumes_at: null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", subscriptionId);

      if (error) throw error;

      toast({
        title: "Subscription resumed",
        description: "The subscription is now active",
      });

      await fetchSubscriptions();
    } catch (err) {
      console.error("Error resuming subscription:", err);
      toast({
        title: "Error",
        description: "Failed to resume subscription",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Calculate summary stats
  const stats = {
    total: subscriptions.length,
    active: subscriptions.filter((s) => s.status === "active" || s.status === "trialing").length,
    cancelled: subscriptions.filter((s) => s.status === "cancelled").length,
    paused: subscriptions.filter((s) => s.status === "paused").length,
    pastDue: subscriptions.filter((s) => s.status === "past_due").length,
    monthlyRecurring: subscriptions
      .filter((s) => s.status === "active" || s.status === "trialing")
      .reduce((sum, s) => {
        // Normalize to monthly
        let monthlyAmount = s.amount_cents;
        if (s.interval === "week") {
          monthlyAmount = s.amount_cents * (52 / 12) / s.interval_count;
        } else if (s.interval === "year") {
          monthlyAmount = s.amount_cents / 12 / s.interval_count;
        } else {
          monthlyAmount = s.amount_cents / s.interval_count;
        }
        return sum + monthlyAmount;
      }, 0),
  };

  return {
    subscriptions,
    loading,
    error,
    stats,
    refetch: fetchSubscriptions,
    createSubscription,
    cancelSubscription,
    pauseSubscription,
    resumeSubscription,
  };
}

/**
 * Format the billing interval for display
 */
export function formatBillingInterval(interval: SubscriptionInterval, intervalCount: number): string {
  const config = SUBSCRIPTION_INTERVAL_CONFIG[interval];
  if (intervalCount === 1) {
    return config.label.toLowerCase();
  }
  return `every ${intervalCount} ${config.plural}`;
}
